/**
 * FE-U2 — compact presence summary from GET /devices/{id}/presence-diagnose.
 * Human summary only; not a raw JSON dump.
 */

import { buildContractPath } from "./api/contract-client.js";

const SUSPECT_LABELS = {
  B0_row_stale_unknown_cause: "Stale presence · cause unclear",
  B1_ghost_suppressed: "Telemetry suppressed (ghost / unbound)",
  B2_mqtt_broker_reject: "Likely device-link broker reject",
  B3_no_device_state_row: "No board presence yet",
  B4_heartbeat_interval_gt_threshold: "Heartbeat interval vs offline threshold",
  B5_retained_lwt_offline: "Retained LWT offline signal",
  B6_cache_or_ui_lag: "Possible cache / UI lag",
  B7_boot_sync_or_cloud_unreachable: "Boot-sync / cloud unreachable",
  B8_http_board_live: "HTTP board-report live",
};

/**
 * @param {string} deviceId
 * @returns {string}
 */
export function buildGetDevicePresenceDiagnosePath(deviceId) {
  return buildContractPath("get__devices_p_presence-diagnose", {
    pathParams: [String(deviceId || "").trim()],
  });
}

/**
 * @param {number} ageS
 * @returns {string}
 */
function formatAgeS(ageS) {
  const n = Number(ageS);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 60) return `${Math.floor(n)}s ago`;
  if (n < 3600) return `${Math.floor(n / 60)}m ago`;
  if (n < 86400) return `${Math.floor(n / 3600)}h ago`;
  return `${Math.floor(n / 86400)}d ago`;
}

/**
 * @param {Record<string, unknown> | null | undefined} diag
 * @returns {{
 *   onlineLabel: string,
 *   headline: string,
 *   lastSeen: string,
 *   hint: string,
 *   badgeCls: string,
 * }}
 */
export function summarizePresenceDiagnose(diag) {
  if (!diag || typeof diag !== "object") {
    return {
      onlineLabel: "unknown",
      headline: "Presence diagnose unavailable",
      lastSeen: "—",
      hint: "Try again in a moment.",
      badgeCls: "offline",
    };
  }
  const online = !!diag.is_online;
  const evidence = /** @type {Record<string, unknown>} */ (diag.evidence || {});
  const decision = /** @type {Record<string, unknown>} */ (diag.decision || {});
  const suspect = String(diag.suspect || "").trim();
  const nextAction = String(diag.next_action || "").trim();
  const headline =
    SUSPECT_LABELS[suspect] ||
    (suspect ? suspect.replace(/_/g, " ") : online ? "Online" : "Offline");
  const lastSeen = formatAgeS(evidence.last_signal_age_s);
  const reason = String(decision.effective_online_reason || "").trim();
  const pathHint = reason ? `Signal path: ${reason.replace(/_/g, " ")}` : "";
  const hintParts = [nextAction, pathHint].filter(Boolean);
  return {
    onlineLabel: online ? "online" : "offline",
    headline,
    lastSeen,
    hint: hintParts.join(" · ") || "No further hint.",
    badgeCls: online ? "online" : "offline",
  };
}

/**
 * Static shell for the presence card (body filled by wire).
 * @returns {string}
 */
export function devicePresenceDiagnosePanelHtml() {
  return `
    <div class="card" id="devicePresenceDiagPanel" aria-label="Presence diagnose">
      <div class="row" style="align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap">
        <h3 style="margin:0;font-size:14px">Presence</h3>
        <button type="button" class="btn secondary sm btn-tap" id="devicePresenceDiagRefresh">Refresh</button>
      </div>
      <p class="muted" style="margin:6px 0 8px;font-size:12px;line-height:1.45">
        Root cause for Wi‑Fi-up / console-offline · from presence check
      </p>
      <div id="devicePresenceDiagBody" aria-live="polite">
        <p class="muted" style="margin:0">Loading…</p>
      </div>
    </div>`;
}

/**
 * @param {HTMLElement} root
 * @param {{
 *   deviceId: string,
 *   routeSeq?: number,
 *   isRouteCurrent?: (seq: number) => boolean,
 *   api: (path: string, opts?: object) => Promise<any>,
 *   escapeHtml: (s: any) => string,
 * }} ctx
 */
export function wireDevicePresenceDiagnosePanel(root, ctx) {
  const body = root && root.querySelector("#devicePresenceDiagBody");
  const refreshBtn = root && root.querySelector("#devicePresenceDiagRefresh");
  if (!body || typeof ctx.api !== "function") return;
  const escapeHtml = typeof ctx.escapeHtml === "function"
    ? ctx.escapeHtml
    : (v) => String(v == null ? "" : v);
  const deviceId = String(ctx.deviceId || "").trim();
  if (!deviceId) {
    body.innerHTML = `<p class="muted" style="margin:0">Missing device id.</p>`;
    return;
  }

  const renderSummary = (sum) => {
    body.innerHTML = `
      <div class="row" style="gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px">
        <span class="badge ${escapeHtml(sum.badgeCls)}">${escapeHtml(sum.onlineLabel)}</span>
        <strong style="font-size:13px">${escapeHtml(sum.headline)}</strong>
      </div>
      <div class="device-owner-kv" style="margin:0">
        <div><span class="muted">Last signal</span><span>${escapeHtml(sum.lastSeen)}</span></div>
        <div><span class="muted">Hint</span><span>${escapeHtml(sum.hint)}</span></div>
      </div>`;
  };

  const renderBusy = () => {
    body.innerHTML = `<p class="muted" style="margin:0">Server busy (503). Tap Refresh to retry.</p>`;
  };

  const renderDenied = (status) => {
    if (status === 404) {
      body.innerHTML = `<p class="muted" style="margin:0">Board not found for presence diagnose.</p>`;
      return;
    }
    if (status === 403) {
      body.innerHTML = `<p class="muted" style="margin:0">No permission to view presence diagnose for this board.</p>`;
      return;
    }
    body.innerHTML = `<p class="muted" style="margin:0">Could not load presence diagnose.</p>`;
  };

  const load = async () => {
    body.innerHTML = `<p class="muted" style="margin:0">Loading…</p>`;
    try {
      const diag = await ctx.api(buildGetDevicePresenceDiagnosePath(deviceId), {
        timeoutMs: 12000,
        retries: 0,
      });
      if (typeof ctx.isRouteCurrent === "function" && ctx.routeSeq != null) {
        if (!ctx.isRouteCurrent(ctx.routeSeq)) return;
      }
      renderSummary(summarizePresenceDiagnose(diag));
    } catch (err) {
      if (typeof ctx.isRouteCurrent === "function" && ctx.routeSeq != null) {
        if (!ctx.isRouteCurrent(ctx.routeSeq)) return;
      }
      const msg = String((err && err.message) || err || "");
      const m = msg.match(/^(\d{3})\b/);
      const status = m ? Number(m[1]) : Number((err && (err.status || err.statusCode)) || 0);
      if (status === 503) {
        renderBusy();
        return;
      }
      renderDenied(status || 0);
    }
  };

  if (refreshBtn && refreshBtn.dataset.bound !== "1") {
    refreshBtn.dataset.bound = "1";
    refreshBtn.addEventListener("click", () => {
      void load();
    });
  }
  void load();
}
